import { useMutation, useQuery } from '@apollo/client'
import { ALL_FIXED_HOURLY_ADDONS } from './queries'
import { useConfirm, useNodes, usePermissions } from '@/utils/hooks'
import { OldAddButton } from '@/modules/companies'
import { Table } from '@/components_deprecated/Table'
import { format, formatISO, parseISO } from 'date-fns'
import styled from 'styled-components'
import {
  Icon,
  Loader,
  usePrompt,
  useToast,
} from '@ur-solutions/react-components'
import { Card } from '@/components_deprecated/Card'
import { isMobileOnly } from 'react-device-detect'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { FixedHourlyAddonNode } from './types.graphql'
import {
  CREATE_FIXED_HOURLY_ADDON,
  DELETE_FIXED_HOURLY_ADDON,
  PATCH_FIXED_HOURLY_ADDON,
} from './mutations'
import { PERMISSIONS } from '@/utils/permissions'
import { H3 } from '@/components'
import type { ConfigureFixedAddonPromptForm } from './ConfigureFixedAddonPrompt'
import { ConfigureFixedAddonPrompt } from './ConfigureFixedAddonPrompt'

const Wrapper = styled(Card)`
  header {
    grid-area: header;

    display: flex;
    justify-content: space-between;
    align-items: center;

    h2 {
      margin: 0;
      font-size: 1.6rem;
    }
    div {
      display: flex;

      button + button {
        margin-left: 1rem;
      }
    }
  }
`

interface FixedHourlyAddonsProps {
  typeId: string
  type: 'user' | 'salaryWorkTime'
}

export const FixedHourlyAddons: React.JSX.CFC<FixedHourlyAddonsProps> = ({
  typeId,
  type,
}) => {
  const translations = useTranslate({
    timeAddons: 'salary.time-addons',
    overrideOtherAddons: 'salary.override-other-addons',

    start: 'common.start',
    end: 'common.end',
    salaryCode: 'common.salary-code',
    edit: 'common.edit',
    delete: 'common.delete',

    deleteTimeOverride: 'salary.delete-time-addon',
    deleteTimeOverrideConfirm: 'salary.delete-time-addon-confirm',

    toast: {
      successCreate: 'salary.time-addon-create-success',
      successUpdate: 'salary.time-addon-update-success',
      successDelete: 'salary.time-addon-delete-success',
    },
  })

  const addPrompt = usePrompt()
  const addToast = useToast()
  const confirm = useConfirm()
  const { hasPermissions } = usePermissions()

  const canAdd = hasPermissions(PERMISSIONS.salary.add.timeaddon, true)

  const canEdit = hasPermissions(PERMISSIONS.salary.change.timeaddon, true)
  const canDelete = hasPermissions(PERMISSIONS.salary.delete.timeaddon, true)

  const { data, loading } = useQuery(ALL_FIXED_HOURLY_ADDONS, {
    variables: {
      [type]: typeId,
    },
  })

  const refetchQueries = [
    {
      query: ALL_FIXED_HOURLY_ADDONS,
      variables: {
        [type]: typeId,
      },
    },
    'DriverActivityPeriodReport',
    'DriverActivityPeriodReportShallow',
    'AllDynamicColumnsQuery',
    'DriverActivityDayReport',
  ]

  const [createFixedHourlyAddon] = useMutation(CREATE_FIXED_HOURLY_ADDON, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successCreate)
    },
  })

  const [patchFixedHourlyAddon] = useMutation(PATCH_FIXED_HOURLY_ADDON, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successUpdate)
    },
  })

  const [deleteFixedHourlyAddon] = useMutation(DELETE_FIXED_HOURLY_ADDON, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successDelete)
    },
  })

  const fixedHourlyAddons = useNodes(data?.allFixedHourlyAddons)

  const handleAdd = async () => {
    const initialData = {
      salaryCode: null,
      comment: '',
      validFrom: null,
      validTo: null,
    }
    const { data } = await addPrompt<ConfigureFixedAddonPromptForm | null>(
      resolve => (
        <ConfigureFixedAddonPrompt
          initialData={initialData}
          onSubmit={resolve}
        />
      )
    )

    if (!data) return

    let patchData: {
      validFrom?: string
      validTo?: string
    } = {}

    if (data.validFrom && data.validTo) {
      patchData = {
        validFrom: formatISO(data.validFrom),
        validTo: formatISO(data.validTo),
      }
    } else if (data.validFrom) {
      patchData = {
        validFrom: formatISO(data.validFrom),
      }
    } else if (data.validTo) {
      patchData = {
        validTo: formatISO(data.validTo),
      }
    }
    void createFixedHourlyAddon({
      variables: {
        input: {
          ...patchData,
          salaryCode: data.salaryCode ?? '',
          [type]: typeId,
        },
      },
    })
  }

  const handleEdit = async (addon: FixedHourlyAddonNode) => {
    const initialData = {
      salaryCode: addon.salaryCode?.id ?? '',
      comment: '',
      validFrom: addon.validFrom ? parseISO(addon.validFrom) : null,
      validTo: addon.validTo ? parseISO(addon.validTo) : null,
    }
    const { data } = await addPrompt<ConfigureFixedAddonPromptForm | null>(
      resolve => (
        <ConfigureFixedAddonPrompt
          initialData={initialData}
          onSubmit={resolve}
        />
      )
    )

    if (!data) return

    let datetimeData: {
      validFrom?: string
      validTo?: string
    } = {}

    if (data.validFrom && data.validTo) {
      datetimeData = {
        validFrom: formatISO(data.validFrom),
        validTo: formatISO(data.validTo),
      }
    } else if (data.validFrom) {
      datetimeData = {
        validFrom: formatISO(data.validFrom),
      }
    } else if (data.validTo) {
      datetimeData = {
        validTo: formatISO(data.validTo),
      }
    }

    void patchFixedHourlyAddon({
      variables: {
        id: addon.id,
        input: {
          ...datetimeData,
          salaryCode: data.salaryCode,
        },
      },
    })
  }

  const handleDelete = async (addon: FixedHourlyAddonNode) => {
    const { data } = await confirm(
      translations.deleteTimeOverrideConfirm,
      translations.deleteTimeOverride
    )

    if (!data) return

    void deleteFixedHourlyAddon({
      variables: {
        id: addon.id,
      },
    })
  }

  const numUtilColumns = (canEdit ? 1 : 0) + (canDelete ? 1 : 0)

  return (
    <Wrapper>
      <header>
        <H3>{translations.timeAddons}</H3>
        {loading && <Loader.Spinner />}
        {!loading && !isMobileOnly && canAdd && (
          <OldAddButton adding={false} onClick={handleAdd} />
        )}
      </header>

      {!loading && (
        <Table
          noShadow
          noBorders
          noHeaderMargin
          noRowMargin
          rowHoverColor="secondary"
        >
          <thead>
            <tr>
              <th>{translations.salaryCode}</th>
              <th></th>
              {(canEdit || canDelete) && <th colSpan={numUtilColumns}></th>}
            </tr>
          </thead>
          <tbody>
            {fixedHourlyAddons.map(fixedHourlyAddon => {
              const dateText =
                fixedHourlyAddon.validFrom && fixedHourlyAddon.validTo
                  ? `${format(
                      parseISO(fixedHourlyAddon.validFrom),
                      'dd.MM.yyyy'
                    )} - ${format(
                      parseISO(fixedHourlyAddon.validTo),
                      'dd.MM.yyyy'
                    )}`
                  : fixedHourlyAddon.validFrom
                  ? `${format(
                      parseISO(fixedHourlyAddon.validFrom),
                      'dd.MM.yyyy'
                    )} - `
                  : fixedHourlyAddon.validTo
                  ? ` - ${format(
                      parseISO(fixedHourlyAddon.validTo),
                      'dd.MM.yyyy'
                    )}`
                  : ''
              return (
                <tr key={fixedHourlyAddon.id}>
                  <td>
                    {fixedHourlyAddon.salaryCode
                      ? fixedHourlyAddon.salaryCode.name
                      : '-'}
                  </td>
                  <td>{dateText}</td>
                  {canEdit && (
                    <td width="1px">
                      <Icon
                        icon="edit"
                        cursor="pointer"
                        color="gray6"
                        hoverColor="secondary"
                        onClick={() => handleEdit(fixedHourlyAddon)}
                      />
                    </td>
                  )}
                  {canDelete && (
                    <td width="1px">
                      <Icon
                        icon="times"
                        cursor="pointer"
                        color="gray6"
                        hoverColor="red"
                        onClick={() => handleDelete(fixedHourlyAddon)}
                      />
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </Table>
      )}
    </Wrapper>
  )
}
